import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, Typography, CircularProgress,
    Autocomplete, TextField
} from '@mui/material';
import axios from 'axios';
import baseURL from '../../config';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const TransferVesselModal = ({ open, onClose, contractId }) => {
    const [loading, setLoading] = useState(false);
    const [vesselOwners, setVesselOwners] = useState([]);
    const [vesselDetails, setVesselDetails] = useState([]);
    const [selectedVesselOwner, setSelectedVesselOwner] = useState(null);
    const [selectedVessel, setSelectedVessel] = useState(null);
    const [transferReason, setTransferReason] = useState('');

    const token = localStorage.getItem('token');

    useEffect(() => {
        if (open) {
            fetchVesselOwners();
            setTransferReason('');
            setSelectedVesselOwner(null);
            setSelectedVessel(null);
            setVesselDetails([]);
        }
    }, [open]);

    const fetchVesselOwners = async () => {
        try {
            const res = await axios.get(`${baseURL}/api/getvesselownerforcontract`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setVesselOwners(res.data.data || []);
        } catch (error) {
            console.error("Error fetching vessel owners:", error);
            toast.error('Failed to load vessel owners.');
        }
    };

    const fetchShipDetailsByOwnerId = async (id) => {
        try {
            const response = await axios.post(`${baseURL}/api/vesselformbyagentById/${id}`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setVesselDetails(response.data || []);
        } catch (error) {
            console.error("Error fetching ship details:", error);
            toast.error('Error fetching vessel details.');
        }
    };

    const handleChange = (event, value, field) => {
        if (field === 'vesselOwner') {
            setSelectedVesselOwner(value);
            setSelectedVessel(null);
            if (value?._id) {
                fetchShipDetailsByOwnerId(value._id);
            } else {
                setVesselDetails([]);
            }
        } else if (field === 'ShipDetails') {
            setSelectedVessel(value);
        }
    };

    const handleTransferSubmit = async () => {
        if (!selectedVessel || !transferReason.trim()) {
            toast.error(!transferReason.trim()
                ? 'Please enter a reason for transfer.'
                : 'Please select a vessel.');
            return;
        }

        try {
            setLoading(true);
            await axios.post(`${baseURL}/api/transfercrewaftersignon/postTransferCrewAfterSignOn`, {
                contractId,
                newVesselId: selectedVessel._id,
                reasonForTransfer: transferReason.trim()
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            toast.success('Crew transferred successfully!');
            setTimeout(() => {
                onClose();
            }, 1000);
        } catch (error) {
            console.error("Transfer error:", error);
            toast.error('Failed to transfer crew.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Transfer Vessel</DialogTitle>
            <DialogContent>
                <Autocomplete
                    options={vesselOwners}
                    value={selectedVesselOwner}
                    getOptionLabel={(option) => option.company_shortname || ''}
                    isOptionEqualToValue={(option, value) => option._id === value?._id}
                    onChange={(event, value) => handleChange(event, value, "vesselOwner")}
                    renderInput={(params) => (
                        <TextField {...params} label="Vessel Owner" required sx={{ mt: 2 }} />
                    )}
                />

                <Autocomplete
                    options={vesselDetails}
                    value={selectedVessel}
                    getOptionLabel={(option) => option.vesselname || ''}
                    isOptionEqualToValue={(option, value) => option._id === value?._id}
                    onChange={(event, value) => handleChange(event, value, "ShipDetails")}
                    renderInput={(params) => (
                        <TextField {...params} label="Vessel" required sx={{ mt: 2 }} />
                    )}
                />

                <TextField
                    label="Transfer Reason"
                    fullWidth
                    multiline
                    minRows={3}
                    sx={{ mt: 2 }}
                    value={transferReason}
                    onChange={(e) => setTransferReason(e.target.value)}
                    required
                />
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} disabled={loading}>Cancel</Button>
                <Button
                    onClick={handleTransferSubmit}
                    color="primary"
                    variant="contained"
                    disabled={loading}
                >
                    {loading ? <CircularProgress size={20} color="inherit" /> : 'Confirm Transfer'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default TransferVesselModal;